import { useQuery } from '@apollo/client'
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import {
  cartLineItemsOf,
  GET_CART,
  GET_PRODUCT_BY_SKU,
  GET_SESSION,
  screenPath,
  setSessionData,
  toCartUnitCount,
  toCatalogCardModel,
  toCatalogItemInputs,
  toQuantitiesBySku,
  type IGetCart,
  type IGetCartVariables,
  type IGetProductBySku,
  type IGetProductBySkuVariables,
  type IProductCatalog,
  type ISession,
  type ISessionVariables,
} from '@commerce/core'
import { ActionBar, Screen, TopBar } from '../design'
import { QuantityStepper } from '../components/QuantityStepper'
import { SimilarProducts } from '../components/SimilarProducts'
import { useAppDispatch } from '../hooks/useAppDispatch'
import { useAppSelector } from '../hooks/useAppSelector'
import { useCounterRules } from '../hooks/useCounterRules'
import { pendingUnitsDelta, useDebouncedCartQuantity } from '../hooks/useDebouncedCartQuantity'
import { usePresentationContext } from '../hooks/usePresentationContext'
import { useRelatedProducts } from '../hooks/useRelatedProducts'
import { useBackInStockNotice } from '../hooks/useBackInStockNotice'

/**
 * One product.
 *
 * It reuses `toCatalogItemInputs` rather than mapping the product itself: the
 * rules for stock, images and promotion badges are the same rules the grid
 * applies, and a detail page that resolved them differently would be a second
 * answer to the same question. The single product is wrapped in a one-category
 * list because that is the shape the mapper reads.
 */
export const ProductDetailPage = () => {
  const { slug = '', sessionId = '', sku = '' } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const presentation = usePresentationContext()
  const promotionConfig = useAppSelector((state) => state.config.config)
  // Whether this customer's hide-prices rule has been resolved at all. See the
  // price block below for why the page waits for it.
  const priceRuleKnown = useAppSelector((state) => state.config.sessionData !== undefined)
  const { pendingBySku, changeQuantity, error: writeError } = useDebouncedCartQuantity()

  const { data, loading, error } = useQuery<IGetProductBySku, IGetProductBySkuVariables>(
    GET_PRODUCT_BY_SKU,
    { variables: { storefrontName: slug, sessionId, sku } },
  )

  // The cart is queried separately so the page can say what is already in it.
  // A failure here must not take the product down with it, so its error is
  // deliberately unused: no quantity is a worse page, not a broken one.
  const { data: cartData } = useQuery<IGetCart, IGetCartVariables>(GET_CART, {
    variables: { cartStorefrontName: slug, cartSessionUid: sessionId },
  })

  /**
   * The session, and only the session.
   *
   * This used to be `GET_INITIAL_CATALOG_VALUES` — the whole catalog — for two
   * reasons: the page needed a session to know whether this customer may see
   * prices, and "similar products" was picked client-side out of that catalog.
   * The second reason is gone (`useRelatedProducts` asks the server), and the
   * first never needed more than this: a deep-linked product page downloading
   * every product the storefront sells, to render one, was the price of the
   * heuristic.
   */
  const { data: sessionData } = useQuery<ISession, ISessionVariables>(GET_SESSION, {
    variables: { storefrontName: slug, sessionUid: sessionId },
    fetchPolicy: 'cache-first',
  })

  // "Instead of this" and "with this", both from the server. Empty means the
  // row is not drawn — see the hook for why nothing is invented here.
  const { alternatives, companions } = useRelatedProducts(sku)

  // What an out-of-stock product can offer instead of a disabled button.
  const notice = useBackInStockNotice(sku)

  const session = sessionData?.session
  useEffect(() => {
    if (session) dispatch(setSessionData(session))
  }, [dispatch, session])

  const product = data?.getProductBySKU
  // The cart's lines, flat or grouped — a grouped storefront (`prueba`) would
  // otherwise leave this page insisting the product is not in the cart.
  const cartLines = useMemo(() => cartLineItemsOf(cartData?.cart), [cartData])
  // What the cart has confirmed, then what the shopper has tapped on top of
  // it — same overlay the catalog grid uses, so a burst of taps is visible
  // immediately while the debounced mutation waits for it to end.
  const committedBySku = useMemo(() => toQuantitiesBySku(cartLines), [cartLines])
  const quantitiesBySku = useMemo(
    () => ({ ...committedBySku, ...pendingBySku }),
    [committedBySku, pendingBySku],
  )

  const model = useMemo(() => {
    if (!product) return undefined
    const [item] = toCatalogItemInputs({
      categories: [{ productsPrices: [product] }],
      quantitiesBySku,
      config: promotionConfig,
      activePromotions: cartData?.cart?.activePromotions,
    })
    return item ? toCatalogCardModel(item, presentation) : undefined
  }, [product, quantitiesBySku, cartData, promotionConfig, presentation])

  /**
   * A row of products, mapped exactly the way the grid maps its cards — same
   * stock rules, same placeholder, same promotion badges. A related row that
   * priced or badged differently would be a second answer to the same question.
   */
  const toRow = useMemo(
    () => (products: IProductCatalog[]) =>
      toCatalogItemInputs({
        categories: [{ category: 'Relacionados', productsPrices: products }],
        quantitiesBySku,
        config: promotionConfig,
        activePromotions: cartData?.cart?.activePromotions,
      }).map((item) => toCatalogCardModel(item, presentation)),
    [quantitiesBySku, cartData, promotionConfig, presentation],
  )

  const similar = useMemo(() => toRow(alternatives), [toRow, alternatives])
  const boughtTogether = useMemo(() => toRow(companions), [toRow, companions])

  const unitsInCart = useMemo(
    () => toCartUnitCount(cartLines) + pendingUnitsDelta(pendingBySku, committedBySku),
    [cartLines, pendingBySku, committedBySku],
  )
  const rules = useCounterRules()

  // The label is the wording the catalog's cart button already uses, which also
  // sidesteps the "1 artículos" a counted label would need a plural rule for.
  const cartButton = (
    <IconButton
      component={RouterLink}
      to={screenPath('cart', { slug, sessionId })}
      aria-label={`Carrito (${unitsInCart})`}
    >
      <Badge badgeContent={unitsInCart} color="primary">
        {/* The design's own cart glyph. There is no icon package in this app and
            the rest of the design system draws its glyphs the same way. */}
        <Box
          component="svg"
          aria-hidden
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          sx={{ width: 20, height: 20 }}
        >
          <circle cx="9" cy="20" r="1.4" />
          <circle cx="17" cy="20" r="1.4" />
          <path d="M3 3h2.2l2.4 12.2a1.6 1.6 0 0 0 1.6 1.3h7.9a1.6 1.6 0 0 0 1.6-1.3L20.5 7H6" />
        </Box>
      </Badge>
    </IconButton>
  )

  // The bar is drawn in every state on purpose: a shopper who opened the wrong
  // product must be able to leave before it finishes loading. `backTo` rather
  // than `onBack` because the destination is a known screen, not a history pop.
  const topBar = (
    <TopBar
      title="Producto"
      backTo={screenPath('catalog', { slug, sessionId })}
      trailing={cartButton}
    />
  )

  if (loading) {
    return (
      <Screen testId="product-detail-page">
        {topBar}
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Cargando producto" />
        </Box>
      </Screen>
    )
  }

  if (error) {
    return (
      <Screen testId="product-detail-page">
        {topBar}
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      </Screen>
    )
  }

  // The storefront may hide prices outright, and a customer may be barred from
  // them by their session. Until the session has landed neither is known, and
  // showing a price that a later render takes away is the one defect this page
  // must not have — so the price waits rather than guesses.
  const showPrice = model?.showPrice === true && priceRuleKnown

  return (
    <Screen testId="product-detail-page">
      {topBar}

      {!model ? (
        <Typography sx={{ p: 3 }}>Producto no encontrado.</Typography>
      ) : (
        <>
          <Stack spacing={2} sx={{ flex: 1, px: 2.5, pt: 2, pb: 2.5 }}>
            {/* A failed write leaves the bar showing its old state and nothing
                else changes, so without this the shopper sees no reason why. */}
            {writeError && <Alert severity="error">{writeError.message}</Alert>}

            <Box
              sx={{
                position: 'relative',
                height: 210,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: '20px',
              }}
            >
              <Box
                component="img"
                src={model.imageUrl}
                alt={model.title}
                sx={{ maxWidth: '70%', maxHeight: '80%', objectFit: 'contain' }}
              />
              {model.promotionBadge && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: '999px',
                    bgcolor: 'success.main',
                    color: 'text.primary',
                    fontSize: 11.5,
                    fontWeight: 700,
                  }}
                >
                  {model.promotionBadge.text}
                </Box>
              )}
            </Box>

            <Stack spacing={0.75}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                {model.title}
              </Typography>
              {/* The design's "pack · categoría" line. The domain has no pack
                  description, so the product's own description fills that half
                  and drops out when it is empty. */}
              {(product?.description || product?.category) && (
                <Typography variant="body2" color="text.secondary">
                  {[product?.description, product?.category].filter(Boolean).join(' · ')}
                </Typography>
              )}
              {showPrice && (
                <Typography variant="h4" component="p" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                  {model.priceText}
                </Typography>
              )}
            </Stack>

            {/* The design also shows "precio sugerido" and "ganarás" per unit.
                `IProductCatalog` carries neither a suggested retail price nor a
                margin, and inventing them would put made-up money on a page a
                shopkeeper prices against — so the two tiles are not built. */}

            {model.quantityInCart > 0 && (
              <Typography variant="body2" color="text.secondary">
                En carrito: {model.quantityInCart}
              </Typography>
            )}
          </Stack>

          <SimilarProducts
            models={similar}
            onOpen={(similarSku) =>
              navigate(screenPath('productDetail', { slug, sessionId, sku: similarSku }))
            }
            onAdd={(similarSku) =>
              changeQuantity(similarSku, (quantitiesBySku[similarSku] ?? 0) + rules.step)
            }
          />

          {/* "With this", not "instead of this". Two questions, two fields, and
              the difference is what a shopkeeper is actually deciding. */}
          <SimilarProducts
            models={boughtTogether}
            title="Se compran juntos"
            onOpen={(companionSku) =>
              navigate(screenPath('productDetail', { slug, sessionId, sku: companionSku }))
            }
            onAdd={(companionSku) =>
              changeQuantity(companionSku, (quantitiesBySku[companionSku] ?? 0) + rules.step)
            }
          />

          <ActionBar>
            {model.isOutOfStock && model.quantityInCart === 0 ? (
              /* An out-of-stock product used to end here, at a button that
                 said "Sin stock" and could not be pressed — leaving the
                 shopkeeper to come back and check by hand.

                 Only when the cart holds none of it. A product can run out
                 *after* it was added — `availableStock` forces `stock: 0` on
                 `outOfStock` no matter what the cart holds — and replacing the
                 stepper with a notice would strand that line: the page says
                 "En carrito: 4" with no way to reduce or remove it. */
              <Stack spacing={1} sx={{ width: '100%' }}>
                {notice.error && <Alert severity="error">{notice.error.message}</Alert>}
                <Button
                  fullWidth
                  variant={notice.requested ? 'outlined' : 'contained'}
                  size="large"
                  disabled={notice.requested || notice.loading}
                  onClick={notice.notifyMe}
                >
                  {notice.requested ? 'Te avisaremos cuando llegue' : 'Avísame cuando llegue'}
                </Button>
                <Typography variant="body2" color="text.secondary" align="center">
                  {notice.requested
                    ? 'Recibirás un aviso en cuanto vuelva a haber existencia.'
                    : 'Sin stock por ahora.'}
                </Typography>
              </Stack>
            ) : model.quantityInCart === 0 ? (
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!model.canAddToCart}
                onClick={() => changeQuantity(model.sku, rules.step)}
                sx={{ justifyContent: 'space-between' }}
              >
                <span>Agregar al carrito</span>
                {/* The design puts the unit price here, not a line total: the
                    quantity is still one step at this point, and a total is the
                    cart's number to compute, not this button's. */}
                {showPrice && <span>{model.priceText}</span>}
              </Button>
            ) : (
              <Stack direction="row" spacing={1.5} alignItems="center">
                {/* The stepper commits an absolute quantity; the debounced
                    hook collapses a burst of commits into one `replace`
                    mutation carrying the last one. */}
                <QuantityStepper
                  value={model.quantityInCart}
                  rules={rules}
                  onCommit={(quantity) => changeQuantity(model.sku, quantity)}
                />
                <Button
                  component={RouterLink}
                  to={screenPath('cart', { slug, sessionId })}
                  variant="contained"
                  size="large"
                  sx={{ flex: 1 }}
                >
                  Ir al carrito
                </Button>
              </Stack>
            )}
          </ActionBar>
        </>
      )}
    </Screen>
  )
}
